import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  AutoCompleteGrouped,
  BoxV2,
  LabraToolTipV2,
  TextAreaWithChips,
} from '@labrav/react-components'
import React, { useState } from 'react'
import { faMagnifyingGlass } from '@fortawesome/pro-regular-svg-icons'
import { Typography } from '@mui/material'
import { FormikProps, useFormikContext } from 'formik'
import { useStyles } from './FlyOutProductDiscoverability.styles'
import { productCategoryData } from '../../../common/utils/OnboardingDataSelect'
import { RequirementLink } from '../../../types/RequirementLink'
import { FormLockedType } from '../../../types/FormLockedType'
import { FlyoutProductSections } from '../../../types/addEditProduct'
import { FlyoutProductDetailsProductDiscoverabilityFaqs } from './FlyoutProductDetailsProductDiscoverabilityFaqs'

export interface Options {
  label: string
  group: string
}

type FlyOutProductDiscoverabilityProps = {
  detailView?: boolean
  detailViewTooltip?: string
}

const getSearchKeywords = (searchKeywords: string | string[]): string[] => {
  if (typeof searchKeywords === 'string') {
    if (!searchKeywords.trim()) {
      return []
    }
    return searchKeywords.split(',').filter(keyword => keyword.trim() !== '')
  }
  return searchKeywords || []
}

export const FlyOutProductDiscoverability: React.FC<
  RequirementLink & FormLockedType & FlyOutProductDiscoverabilityProps
> = ({ isFormLocked, detailView, detailViewTooltip }) => {
  const { classes } = useStyles()
  const { values, errors, touched, handleBlur, setFieldValue } =
    useFormikContext() as FormikProps<FlyoutProductSections>
  const generalInformation = values.generalInformation
  const generalInformationErrors = errors.generalInformation
  const generalInformationTouched = touched.generalInformation

  const keywordsTags = getSearchKeywords(generalInformation.searchKeywords)

  const handleTagsChange = async (newTags: string) => {
    if (newTags && newTags?.includes(',')) {
      const newTagsArray = newTags.split(',')

      let filteredOptions = newTagsArray
        .map(option => option.trim())
        .filter(option => !keywordsTags?.includes(option))

      if (filteredOptions.length > 5 - keywordsTags.length) {
        filteredOptions = newTagsArray.slice(0, 5 - keywordsTags.length)
      }
      const commaSeparatedString = [...keywordsTags, ...filteredOptions].join(
        ','
      )
      await setFieldValue(
        'generalInformation.searchKeywords',
        commaSeparatedString
      )
    } else if (newTags && !keywordsTags?.includes(newTags)) {
      const commaSeparatedString = [...keywordsTags, newTags].join(',')
      setFieldValue('generalInformation.searchKeywords', commaSeparatedString)
    }
  }
  const handleDelete = (value: string) => {
    const filteredOptions = keywordsTags.filter(option => option !== value)
    setFieldValue(
      'generalInformation.searchKeywords',
      filteredOptions.join(',')
    )
  }

  const handleChange = (
    event: React.ChangeEvent<object>,
    newValues: Options[]
  ) => {
    if (generalInformation.productCategories) {
      const labels = newValues.map(option => option.label)
      setFieldValue('generalInformation.productCategories', labels)
    }
  }

  const isDisabled = !!isFormLocked || !!detailView
  const [indexModal, setIndexModal] = useState<number>(1)

  return (
    <BoxV2
      heading={'Product discoverability'}
      headingIcon={
        <FontAwesomeIcon
          icon={faMagnifyingGlass}
          style={{ width: '20px', height: '16px' }}
        />
      }
      secondaryActions={
        <FlyoutProductDetailsProductDiscoverabilityFaqs
          indexModal={indexModal}
          setIndexModal={setIndexModal}
        />
      }
    >
      <>
        <div
          className={classes.sectionContainer}
          data-testid="product-categories"
        >
          <div className={classes.textContainer}>
            <Typography className={classes.sectionHeading}>
              Product categories
            </Typography>
            <Typography className={classes.sectionText}>
              Choose up to 3 relevant categories. Adding categories helps buyers
              discover your product as they browse or search AWS Marketplace.
            </Typography>
          </div>
          <div className={classes.autoCompleteGrouppedWidth}>
            <LabraToolTipV2
              tooltipPlacement="bottom-start"
              description={detailViewTooltip}
              followCursor={detailView}
            >
              <AutoCompleteGrouped
                onChange={handleChange}
                id={'generalInformation.productCategories'}
                label={'Choose up to 3 categories'}
                options={productCategoryData}
                value={getOptionFromLabel(
                  generalInformation.productCategories,
                  productCategoryData
                )}
                error={
                  !!generalInformationErrors?.productCategories &&
                  !!generalInformationTouched?.productCategories
                }
                helperText={
                  (!!generalInformationTouched?.productCategories &&
                    generalInformationErrors?.productCategories) ||
                  ''
                }
                onBlur={handleBlur}
                disabled={isDisabled}
                viewOnly={detailView}
              />
            </LabraToolTipV2>
          </div>
        </div>

        <div className={classes.sectionContainer} data-testid="search-keywords">
          <div className={classes.textContainer}>
            <Typography className={classes.sectionHeading}>
              Keywords for search results
            </Typography>
            <Typography className={classes.sectionText}>
              Enter up to 15 keywords (single words or phrases) that customers
              might use when searching on AWS Marketplace website for your
              product. Each keyword can have up to 50 characters.
            </Typography>
          </div>
          <LabraToolTipV2
            tooltipPlacement="bottom-start"
            description={detailViewTooltip}
            followCursor={detailView}
            className={classes.textFieldWidth}
          >
            <div className={classes.textFieldWidth}>
              <TextAreaWithChips
                id={'generalInformation.searchKeywords'}
                label={
                  'Press enter or comma to add keywords for search results'
                }
                onBlur={handleBlur}
                onChange={handleTagsChange}
                handleDelete={handleDelete}
                selectedValues={keywordsTags}
                maxChips={15}
                error={
                  !!(
                    generalInformationErrors?.searchKeywords &&
                    generalInformationTouched?.searchKeywords
                  )
                }
                helperText={
                  (generalInformationTouched?.searchKeywords &&
                    generalInformationErrors?.searchKeywords) ||
                  ''
                }
                disabled={isDisabled}
                viewOnly={detailView}
              />
            </div>
          </LabraToolTipV2>
        </div>
      </>
    </BoxV2>
  )
}

const getOptionFromLabel = (
  labels: string[],
  options: Options[]
): Options[] => {
  return options.filter(({ label }) => {
    return labels.includes(label)
  })
}
